import { FormEvent, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useAssignLeadershipMutation,
  useGetChapterDetailsQuery,
  useGetChapterDietsQuery,
  useRemoveLeadershipMutation,
} from "../api/chapterApi";
import MemberPicker from "../components/MemberPicker";

function isDietActive(d: any) {
  return d?.is_active === true || d?.is_active === 1;
}

function fmtDate(d?: string | null) {
  if (!d) return "—";
  return String(d).slice(0, 10);
}

export default function ChapterDetailsPage() {
  const { id } = useParams();
  const chapterId = String(id);

  const {
    data: chapter,
    isLoading,
    error,
    refetch,
  } = useGetChapterDetailsQuery(chapterId, { skip: !chapterId });

  const { data: diets = [] } = useGetChapterDietsQuery();

  const [assign, { isLoading: assigning }] = useAssignLeadershipMutation();
  const [removeLeadership, { isLoading: removing }] =
    useRemoveLeadershipMutation();

  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const [form, setForm] = useState({
    chapter_diet_id: "",
    chairman_id: null as number | null,
    secretary_id: null as number | null,
  });

  const leaderships = useMemo(() => {
    const list = chapter?.leaderships || [];
    // sort newest first if created_at exists
    return [...list].sort((a: any, b: any) =>
      String(b.created_at || "").localeCompare(String(a.created_at || ""))
    );
  }, [chapter?.leaderships]);

  const selectedDiet = useMemo(
    () => diets.find((d: any) => String(d.id) === String(form.chapter_diet_id)),
    [diets, form.chapter_diet_id]
  );

  const busy = assigning || removing;

  const onAssign = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);

    if (!form.chapter_diet_id) {
      setMsg({ type: "error", text: "Select a diet" });
      return;
    }

    if (!isDietActive(selectedDiet)) {
      setMsg({ type: "error", text: "You can only assign on an active diet." });
      return;
    }

    if (!form.chairman_id && !form.secretary_id) {
      setMsg({ type: "error", text: "Pick at least a chairman or secretary" });
      return;
    }

    if (form.chairman_id === form.secretary_id) {
      setMsg({
        type: "error",
        text: "Chairman and Secretary cannot be the same person",
      });
      return;
    }

    try {
      await assign({
        chapter_id: chapterId,
        chapter_diet_id: form.chapter_diet_id,
        chairman_id: form.chairman_id,
        secretary_id: form.secretary_id,
      }).unwrap();

      setMsg({ type: "success", text: "Leadership assigned successfully" });
      setForm({ chapter_diet_id: "", chairman_id: null, secretary_id: null });
      refetch();
    } catch (err: any) {
      setMsg({
        type: "error",
        text: err?.data?.message || "Assign failed",
      });
    }
  };

  const onRemove = async (leadershipId: string) => {
    const ok = window.confirm("Remove this leadership record?");
    if (!ok) return;

    setMsg(null);
    try {
      await removeLeadership(leadershipId).unwrap();
      setMsg({ type: "success", text: "Leadership removed" });
      refetch();
    } catch (err: any) {
      setMsg({
        type: "error",
        text: err?.data?.message || "Remove failed",
      });
    }
  };

  if (isLoading) return <div className="text-sm text-gray-600">Loading…</div>;
  if (error)
    return <div className="text-sm text-red-600">Failed to load chapter</div>;
  if (!chapter)
    return <div className="text-sm text-gray-600">Chapter not found</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{chapter.name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              State: <span className="font-semibold">{chapter.state_name}</span>
            </p>
          </div>

          <div className="flex gap-2">
            <Link
              to="/chapter/chapters"
              className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
            >
              Back to Chapters
            </Link>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
            >
              Refresh
            </button>
          </div>
        </div>

        {msg && (
          <div
            className={`mt-4 rounded-lg border p-3 text-sm ${
              msg.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {msg.text}
          </div>
        )}
      </div>

      {/* Assign leadership */}
      <div className="bg-white border rounded-xl p-6">
        <div className="font-semibold text-gray-800">Assign Leadership</div>
        <p className="text-sm text-gray-500 mt-1">
          Assign chairman, secretary, or both for a diet. (Diet must be active.)
        </p>

        <form
          onSubmit={onAssign}
          className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4"
        >
          <div>
            <label className="text-sm text-gray-600">Diet</label>
            <select
              value={form.chapter_diet_id}
              onChange={(e) =>
                setForm((s) => ({ ...s, chapter_diet_id: e.target.value }))
              }
              disabled={busy}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select diet</option>
              {diets.map((d: any) => (
                <option key={d.id} value={String(d.id)}>
                  {d.name} {isDietActive(d) ? "(Active)" : "(Closed)"}
                </option>
              ))}
            </select>

            {selectedDiet ? (
              <div className="mt-2 text-xs text-gray-500">
                {fmtDate(selectedDiet.start_date)} →{" "}
                {fmtDate(selectedDiet.end_date)}
              </div>
            ) : null}
          </div>

          <MemberPicker
            label="Chairman"
            valueUserId={form.chairman_id}
            onPickUserId={(userId) =>
              setForm((s) => ({ ...s, chairman_id: userId }))
            }
            disabled={busy}
          />

          <MemberPicker
            label="Secretary"
            valueUserId={form.secretary_id}
            onPickUserId={(userId) =>
              setForm((s) => ({ ...s, secretary_id: userId }))
            }
            disabled={busy}
          />

          <div className="lg:col-span-3 flex justify-end">
            <button
              disabled={busy || !isDietActive(selectedDiet)}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60 inline-flex items-center gap-2"
            >
              {assigning ? (
                <>
                  <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  Assigning…
                </>
              ) : (
                "Assign Leadership"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Leadership timeline */}
      <div className="bg-white border rounded-xl p-6">
        <div className="font-semibold text-gray-800 mb-3">
          Leadership History
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Diet</th>
                <th className="py-2">Chairman</th>
                <th className="py-2">Secretary</th>
                <th className="py-2">Created</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>

            <tbody>
              {leaderships.map((l: any) => (
                <tr key={l.id} className="border-b">
                  <td className="py-3">
                    <div className="font-medium">{l.diet?.name || "—"}</div>
                    <div className="text-xs text-gray-500">
                      {fmtDate(l.diet?.start_date)} →{" "}
                      {fmtDate(l.diet?.end_date)} •{" "}
                      {isDietActive(l.diet) ? "Active" : "Closed"}
                    </div>
                  </td>

                  <td className="py-3">
                    <div className="font-medium">{l.chairman?.name || "—"}</div>
                    <div className="text-xs text-gray-500">
                      {l.chairman?.email || ""}{" "}
                      {l.chairman?.whois ? `• ${l.chairman.whois}` : ""}
                    </div>
                  </td>

                  <td className="py-3">
                    <div className="font-medium">
                      {l.secretary?.name || "—"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {l.secretary?.email || ""}{" "}
                      {l.secretary?.whois ? `• ${l.secretary.whois}` : ""}
                    </div>
                  </td>

                  <td className="py-3 text-gray-600">
                    {fmtDate(l.created_at)}
                  </td>

                  <td className="py-3">
                    <button
                      type="button"
                      onClick={() => onRemove(String(l.id))}
                      disabled={busy}
                      className="px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50 text-xs disabled:opacity-60"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}

              {!leaderships.length && (
                <tr>
                  <td colSpan={5} className="py-6 text-gray-500">
                    No leadership records found for this chapter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
